import { SITE } from '@cardengine/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { Chat } from '../components/Chat.js';
import { GameLog } from '../components/GameLog.js';
import { linkProps } from '../router.js';
import { useStore } from '../store.js';

/** Let the winning mark land before the result goes up. */
const LANDING_MS = 700;

export interface PaperSeat {
  id: string;
  name: string;
  isBot: boolean;
  connected: boolean;
  isYou: boolean;
  /** Whose go it is. */
  isCurrent: boolean;
  /** A short tag beside the name — the mark, in noughts and crosses. */
  badge?: string;
  wins: number;
}

export interface PaperResult {
  title: string;
  body: string;
  /** Colours the modal and decides whether it reads as a win. */
  tone: 'won' | 'lost' | 'drawn';
  /**
   * A closing line under the result — "three boards cleared in this sitting".
   * A solo game has no tally of names to print, and this is what it says
   * instead.
   */
  footnote?: string;
}

export interface PaperRoomProps {
  /** The game's name, for the header. */
  name: string;
  /**
   * Who is sitting here. Empty for a game played alone, which draws no seat
   * strip at all: the strip exists to make it unmistakable whose go it is, and
   * on a board with one player that question does not arise.
   */
  seats: PaperSeat[];
  /** The one-line readout beside the name: whose go it is, or the goal. */
  status: string;
  board: ReactNode;
  finished: boolean;
  /** What to say once it is over. Built by the game, shown here. */
  result: PaperResult | null;
  /**
   * Whether the side panel offers a chat tab. Off for a game played alone,
   * where there is nobody to chat to and the tab is a dead end.
   */
  chat?: boolean;
  /**
   * Which shelf's room this is, which is only ever a surface.
   *
   * The layout, the header, the log panel and the result are the same table
   * either way — all this changes is what the room looks like around the
   * board, so `arcade` can be a dim room with a lit cabinet in it while
   * `paper` stays the pub. The class is all that crosses over; every rule
   * hanging off it lives in that shelf's own stylesheet.
   */
  variant?: 'paper' | 'arcade';
}

/**
 * The table a two-handed paper game is played at.
 *
 * Sibling to `SoloRoom`, and deliberately not a branch inside `GameRoom`. That
 * view is not "the multiplayer screen" — it is the *card* table: a fan of cards
 * in your hand, a pile in the middle, seats round the edge, and a result screen
 * that scores what you were left holding. Noughts and crosses has none of those
 * and would have been holes cut in all four.
 *
 * What it *does* share with a card table is everything that is about sitting
 * with someone rather than about the game: the table code, the log and chat
 * panel, the running tally of who has won what, the way a finished round offers
 * another. Those are here, and the board itself is passed in — which is the
 * same division `SoloRoom` settled on, arrived at again from the other side.
 *
 * Written for the game after this one as much as for this one: nothing below
 * knows what a square is. That paid out at Minesweeper, which needed no change
 * to the table and three small ones at its edges — the seat strip and the chat
 * tab became conditional, and the result modal learned a closing line. All
 * three are the same observation from different angles: this screen was built
 * assuming somebody else was at the table, and a game played alone is the same
 * room with one chair in it.
 */
export function PaperRoom({
  name,
  seats,
  status,
  board,
  finished,
  result,
  chat = true,
  variant = 'paper',
}: PaperRoomProps) {
  const lobby = useStore((s) => s.lobby);
  const session = useStore((s) => s.session);
  const leaveLobby = useStore((s) => s.leaveLobby);
  const playAgain = useStore((s) => s.playAgain);
  const rematch = useStore((s) => s.rematch);
  const setLogPanelOpen = useStore((s) => s.setLogPanelOpen);

  const [panelOpen, setPanelOpen] = useState(
    () => typeof window !== 'undefined' && window.innerWidth >= 900,
  );
  const [tab, setTab] = useState<'log' | 'chat'>('log');
  const showChat = chat && tab === 'chat';
  const [showResult, setShowResult] = useState(false);

  useEffect(() => {
    setLogPanelOpen(panelOpen && !showChat);
    return () => setLogPanelOpen(false);
  }, [panelOpen, showChat, setLogPanelOpen]);

  useEffect(() => {
    if (!finished) {
      setShowResult(false);
      return;
    }
    const timer = setTimeout(() => setShowResult(true), LANDING_MS);
    return () => clearTimeout(timer);
  }, [finished]);

  if (!lobby) {
    return (
      <main className="game">
        <p className="game__loading">Setting up…</p>
      </main>
    );
  }

  const isHost = lobby.hostId === session?.playerId;

  return (
    // The header shows the table code rather than the game, so the game's name
    // is carried here — otherwise the whole screen is unlabelled to anyone
    // navigating by landmark.
    <main className={`game game--${variant}`} aria-label={`${name} table`}>
      <header className="game__header">
        <a className="game__brand" {...linkProps('/')} title={`${SITE.name} home`}>
          <span className="game__brand-mark" aria-hidden="true">
            ♠
          </span>
          <span className="game__brand-name">{SITE.name}</span>
        </a>
        <span className="game__code">Table {lobby.code}</span>
        <span className="game__turn">{status}</span>
        <div className="game__header-actions">
          <button
            type="button"
            className={`btn btn--ghost btn--small ${panelOpen ? 'is-active' : ''}`}
            onClick={() => setPanelOpen((open) => !open)}
            aria-expanded={panelOpen}
          >
            {panelOpen ? 'Hide log' : 'Show log'}
          </button>
          <button type="button" className="btn btn--ghost btn--small" onClick={() => void leaveLobby()}>
            Leave
          </button>
        </div>
      </header>

      <div className={`game__body ${panelOpen ? 'game__body--with-chat' : ''}`}>
        <div className="paper">
          {seats.length > 0 && <Seats seats={seats} />}
          <div className="paper__board">{board}</div>
        </div>

        {panelOpen && (
          <aside className="game__chat">
            <div className="panel">
              <div className="panel__tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={!showChat}
                  className={`panel__tab ${!showChat ? 'is-active' : ''}`}
                  onClick={() => setTab('log')}
                >
                  Game log
                </button>
                {chat && (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={tab === 'chat'}
                    className={`panel__tab ${tab === 'chat' ? 'is-active' : ''}`}
                    onClick={() => setTab('chat')}
                  >
                    Chat
                  </button>
                )}
              </div>
              <div className="panel__body">{showChat ? <Chat /> : <GameLog />}</div>
            </div>
          </aside>
        )}
      </div>

      {showResult && result && (
        <Result
          result={result}
          rounds={lobby.roundsPlayed}
          seats={seats}
          isHost={isHost}
          onPlayAgain={playAgain}
          onRematch={rematch}
          onLeave={() => void leaveLobby()}
        />
      )}
    </main>
  );
}

/**
 * Both players, side by side.
 *
 * A card table shows a seat per player because it has to show how many cards
 * each is holding. Here there is nothing to count, so this is doing one job:
 * making it unmistakable whose go it is, which on a silent grid is otherwise
 * genuinely easy to lose track of.
 */
function Seats({ seats }: { seats: PaperSeat[] }) {
  return (
    <ol className="pseats">
      {seats.map((seat) => (
        <li
          key={seat.id}
          className={`pseat ${seat.isCurrent ? 'pseat--current' : ''} ${
            seat.connected ? '' : 'pseat--away'
          }`}
        >
          {seat.badge && (
            <span className="pseat__badge" aria-hidden="true">
              {seat.badge}
            </span>
          )}
          <span className="pseat__name">
            {seat.name}
            {seat.isYou && <span className="seats__tag">you</span>}
            {seat.isBot && <span className="seats__tag">AI</span>}
          </span>
          {seat.wins > 0 && (
            <span className="pseat__wins" aria-label={`${seat.wins} ${seat.wins === 1 ? 'win' : 'wins'}`}>
              {seat.wins}
            </span>
          )}
          {/* Announced rather than only coloured, so it reaches a screen reader
              as well as an eye. */}
          {seat.isCurrent && <span className="pseat__turn">to play</span>}
          {!seat.connected && <span className="pseat__away-tag">away</span>}
        </li>
      ))}
    </ol>
  );
}

function Result({
  result,
  rounds,
  seats,
  isHost,
  onPlayAgain,
  onRematch,
  onLeave,
}: {
  result: PaperResult;
  rounds: number;
  seats: PaperSeat[];
  isHost: boolean;
  onPlayAgain: () => void;
  onRematch: () => void;
  onLeave: () => void;
}) {
  const tally = seats.filter((seat) => seat.wins > 0);

  return (
    <div className={`modal modal--${result.tone}`} role="dialog" aria-modal="true" aria-label="Result">
      <div className="modal__panel">
        <header className="modal__head">
          <h2 className="modal__title">{result.title}</h2>
          <span className="modal__round">Round {rounds}</span>
        </header>

        <p className="modal__hint">{result.body}</p>
        {result.footnote && <p className="modal__hint">{result.footnote}</p>}

        {tally.length > 0 && (
          <ol className="results">
            {seats.map((seat) => (
              <li key={seat.id} className="results__row">
                <span className="results__name">
                  {seat.name}
                  {seat.isYou && <span className="seats__tag">you</span>}
                </span>
                <span className="results__wins" aria-label={`${seat.wins} ${seat.wins === 1 ? 'win' : 'wins'}`}>
                  {seat.wins}
                </span>
              </li>
            ))}
          </ol>
        )}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={onLeave}>
            Leave
          </button>
          {isHost ? (
            <div className="modal__actions-group">
              <button type="button" className="btn btn--secondary" onClick={onRematch}>
                Change settings
              </button>
              <button type="button" className="btn btn--primary" onClick={onPlayAgain} autoFocus>
                Play again
              </button>
            </div>
          ) : (
            <p className="modal__hint">Waiting for the host to start another…</p>
          )}
        </div>
      </div>
    </div>
  );
}
